/**
 * Decides whether to show a blur overlay on partner-gated content. Client-side only — the
 * underlying content already reached the browser regardless; this is cosmetic, not access control.
 */
'use client';

import { useMemo } from 'react';
import logout from '@/utils/auth/common/logout';
import { storePreviousPath } from '@/utils/auth/common/auth-storage';
import { useHasPartnerTag } from '@/hooks/useHasPartnerTag';
import { useAuthUser } from '@/lib/auth/common/useSession';

interface OverlayState {
  show: boolean;
}

interface UsePartnerOverlayOptions {
  isPartnerContent?: boolean;
}

const DEFAULT_OVERLAY_STATE: OverlayState = { show: false };

export function usePartnerOverlay({
  isPartnerContent = false,
}: UsePartnerOverlayOptions = {}) {
  const { isLoggedIn } = useAuthUser();
  // null while the auth verdict is still resolving — used below to hold the overlay back.
  const isPartner = useHasPartnerTag();

  // Derived rather than stored: previously this was effect-written state re-read from
  // localStorage, which flashed a stale answer before the effect ran and didn't react to
  // login/logout. useAuthUser is already reactive, so useMemo is enough.
  const overlayState = useMemo<OverlayState>(() => {
    // Wait until auth is known, else a logged-in partner sees the overlay flash on first render.
    if (!isPartnerContent || isPartner === null) return DEFAULT_OVERLAY_STATE;

    // Not logged in, or logged in without a partner tag — either way, show the overlay.
    return { show: !isPartner };
  }, [isPartnerContent, isPartner]);

  /** Logs out and saves the current path for post-login redirect. */
  const handleLogout = async () => {
    if (typeof window === 'undefined') return;
    if (!isLoggedIn) return;

    storePreviousPath(window.location.pathname + window.location.search);
    await logout();
    window.localStorage.removeItem('assessment');
  };

  return {
    overlayState,
    handleLogout,
  };
}
